import { CourseStatus } from "@/lib/constants";
import React from "react";
import { SizableText, YStack } from "tamagui";

const LABELS: Record<number, string> = {
  [CourseStatus.Retired]: "RETIRED",
  [CourseStatus.Draft]: "DRAFT",
  [CourseStatus.Staging]: "STAGING",
};

const COLORS: Record<number, string> = {
  [CourseStatus.Retired]: "#616161",
  [CourseStatus.Draft]: "#F57C00",
  [CourseStatus.Staging]: "#7B1FA2",
};

export function statusBadgeLabel(status?: number | null): string | null {
  if (status == null) return null;
  return LABELS[status] ?? null; // Published (3) -> no badge
}

type StatusBadgeProps = {
  status?: number | null;
  /** Only render when true — typically the allowlisted-draft-viewer flag. */
  visible?: boolean;
  /**
   * Absolutely position as an overlay so it never affects surrounding layout
   * (critical: badges must not shift any UI). The nearest positioned ancestor
   * must be relative.
   */
  overlay?: boolean;
};

/**
 * Small DRAFT / RETIRED / STAGING pill shown only to allowlisted draft viewers
 * on non-published content. Renders nothing for published content or for
 * non-viewers, so it can be dropped in anywhere without affecting other users.
 */
export function StatusBadge({
  status,
  visible = true,
  overlay = false,
}: StatusBadgeProps) {
  const label = statusBadgeLabel(status);
  if (!visible || !label) return null;
  const bg = COLORS[status as number] ?? "#616161";
  return (
    <YStack
      {...(overlay
        ? ({ position: "absolute", top: 8, left: 8, zIndex: 50 } as const)
        : {})}
      backgroundColor={bg}
      paddingHorizontal={8}
      paddingVertical={2}
      borderRadius={4}
      pointerEvents="none"
    >
      <SizableText
        color="white"
        fontSize={10}
        fontWeight="700"
        letterSpacing={0.5}
      >
        {label}
      </SizableText>
    </YStack>
  );
}
